// Observability funnel for the Adyen card-add (tokenization) flow — CPS-26589,
// Milestone B, Task 11. A thin wrapper over the app's Datadog RUM action helper
// (the same DdRum.addAction surface used by utils/analytics.ts) that emits one
// stable RUM action per funnel transition: `adyen_cardadd_<event>`.
//
// SECURITY — this wrapper is the redaction boundary for the funnel. Adyen card
// flows carry PCI/PII-adjacent material (encrypted card data, browser
// fingerprints, 3DS payloads, opaque paymentData blobs). Rather than chase a
// deny-list of sensitive keys (easy to miss one, and nested objects defeat a
// shallow strip), the wrapper uses an ALLOW-LIST: only a small set of known-safe,
// low-cardinality attributes is ever forwarded to RUM; everything else the caller
// passes is dropped. Adding a new attribute is a deliberate edit here, reviewed
// against this contract — the default for any unrecognised key is "do not emit".

import { DdRum, RumActionType } from '@datadog/mobile-react-native';

import Logger from './Logger';

// The funnel events, in roughly the order they fire across the flow:
//   init            adyenInitCardAdd issued (entry hook)
//   methods         /paymentMethods discovery started
//   submit          create-payment submitted (sheet onSubmit)
//   action_start    an SDK action (3DS2/redirect) was returned and is handled
//   action_complete additional-details submitted after an action
//   authorised      terminal AUTHORISED mapping
//   active          card observed Active by the activation poller
//   refused         terminal REFUSED/FAILED mapping
//   cancelled       user dismissed the sheet
//   timeout         activation poll budget elapsed without Active
//   error           any thrown / unmapped failure
export type AdyenFunnelEvent =
  | 'init'
  | 'methods'
  | 'submit'
  | 'action_start'
  | 'action_complete'
  | 'authorised'
  | 'active'
  | 'refused'
  | 'cancelled'
  | 'timeout'
  | 'error';

// The ONLY attribute keys ever forwarded to RUM. Both are low-cardinality,
// non-sensitive descriptors of the funnel itself:
//   refusalReasonCategory — the BFF's categorised refusal bucket (e.g.
//                           'USE_OTHER_CARD'); never a raw refusal reason or PAN.
//   checkoutChannel       — 'WEB' | 'IOS' | 'ANDROID'.
// Anything else a caller passes (paymentMethod, browserInfo, details, action,
// paymentData, …) is dropped before emit. To forward a new attribute, add its
// key here intentionally — never widen this to an open Record.
const ALLOWED_ATTR_KEYS = ['refusalReasonCategory', 'checkoutChannel'] as const;

type AllowedAttrKey = (typeof ALLOWED_ATTR_KEYS)[number];

// Project the caller's attrs down to the allow-list. Only own, defined values
// for allow-listed keys survive; the result is a flat object that can be safely
// serialised into a RUM action context. Note this is intentionally shallow and
// key-scoped: a sensitive value can never ride along nested under an allowed key
// because allowed keys only ever hold scalars here.
const sanitizeAttrs = (
  attrs?: Record<string, unknown>
): Record<AllowedAttrKey, unknown> => {
  const safe = {} as Record<AllowedAttrKey, unknown>;
  if (!attrs) {
    return safe;
  }
  for (const key of ALLOWED_ATTR_KEYS) {
    const value = attrs[key];
    if (value !== undefined && value !== null) {
      safe[key] = value;
    }
  }
  return safe;
};

// Stable RUM action name for a funnel event: `adyen_cardadd_<event>`.
export const adyenFunnelActionName = (event: AdyenFunnelEvent): string =>
  `adyen_cardadd_${event}`;

/**
 * Emit one Adyen card-add funnel event to Datadog RUM.
 *
 * Fire-and-forget: never throws and never blocks the flow. `attrs` is filtered
 * through the allow-list above before emit, so callers may pass the raw flow
 * context without leaking sensitive fields.
 *
 * @param event the funnel transition that just occurred
 * @param attrs optional context; only allow-listed keys are forwarded to RUM
 */
export const trackAdyenCardAdd = (
  event: AdyenFunnelEvent,
  attrs?: Record<string, unknown>
): void => {
  try {
    const name = adyenFunnelActionName(event);
    const context = sanitizeAttrs(attrs);
    // DdRum may be a partial shim on web (webMocks/datadog-web-mock) or absent in
    // some unit-test mocks — guard so a missing addAction is a silent no-op,
    // matching the resilience of utils/analytics.createAction.
    if (typeof DdRum?.addAction === 'function') {
      DdRum.addAction(RumActionType.CUSTOM, name, context);
    }
  } catch (error) {
    // Observability must never break the payment flow.
    Logger.debug(`trackAdyenCardAdd failed for ${event}: ${error}`);
  }
};

export default trackAdyenCardAdd;
